import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/storefront/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("default");

  const category = params.get("category") || "all";

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (category !== "all") p.category = category;
    if (search) p.search = search;
    if (sort !== "default") p.sort = sort;
    const t = setTimeout(() => {
      api.get("/products", { params: p }).then((r) => {
        setProducts(r.data.filter((x) => x.available));
        setLoading(false);
      });
    }, 250);
    return () => clearTimeout(t);
  }, [category, search, sort]);

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("category");
    else next.set("category", slug);
    setParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10 sm:py-14">
      <div className="mb-8">
        <p className="text-goldsoft text-xs uppercase tracking-[0.3em] mb-2">Our Collection</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-choco">The Bakery</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="shop-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cookies, brownies, kunafa..."
            className="w-full h-12 rounded-full bg-white border border-[#E5DFD3] pl-11 pr-4 outline-none focus:ring-2 focus:ring-gold/40"
          />
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <SlidersHorizontal size={18} className="text-choco shrink-0" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="shop-sort" className="h-12 w-full sm:w-52 rounded-full bg-white border-[#E5DFD3]">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="default">Featured</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
              <SelectItem value="name">Name: A to Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-8">
        <button
          data-testid="filter-all"
          onClick={() => setCategory("all")}
          className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
            category === "all" ? "bg-choco text-cream" : "bg-white border border-[#E5DFD3] text-chocodark hover:border-goldsoft"
          }`}
        >
          All
        </button>
        {cats.map((c) => (
          <button
            key={c.id}
            data-testid={`filter-${c.slug}`}
            onClick={() => setCategory(c.slug)}
            className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
              category === c.slug ? "bg-choco text-cream" : "bg-white border border-[#E5DFD3] text-chocodark hover:border-goldsoft"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4] rounded-2xl bg-mutedcream" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div data-testid="shop-empty" className="text-center py-24">
          <p className="font-display text-2xl text-choco">No products found</p>
          <p className="text-muted-foreground mt-2">Try a different search or category.</p>
        </div>
      ) : (
        <div data-testid="shop-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
};

export default Shop;
